import React, { useState, useEffect, useRef } from 'react';
import {
  CreateOrganizerRequest, Organizer, OrganizerKind, toExternalUrl,
  countryOfBundesland,
} from '@radevents/shared';
import BundeslandOptions from './BundeslandOptions';
import { getPlzBundesland } from '../utils/plzBundesland';
import organizerService from '../services/organizerService';
import toast from 'react-hot-toast';
import { ExternalLink } from 'lucide-react';

/**
 * Vollständiges Veranstalter-Formular als Modal — gemeinsam genutzt von der Veranstalter-
 * Verwaltung (anlegen + bearbeiten) und dem Event-Formular („Neu anlegen"), damit beim
 * Anlegen dieselben Felder zur Verfügung stehen wie beim Bearbeiten.
 */

const emptyForm = {
  name: '',
  contactPerson: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  street: '',
  postalCode: '',
  city: '',
  bundesland: '',
  radNetUrl: '',
  impressumUrl: '',
  logoUrl: '',
  stravaClubUrl: '',
  instagramUrl: '',
  facebookUrl: '',
  komootUrl: '',
  orgKind: 'UNKNOWN' as OrganizerKind,
  active: true,
};

interface Props {
  show: boolean;
  /** Vorhandener Veranstalter zum Bearbeiten; null/undefined = neu anlegen. */
  organizer?: Organizer | null;
  onClose: () => void;
  onSaved: (organizer: Organizer) => void;
  /**
   * Ersetzt den direkten Speicheraufruf. Ohne diese Prop schreibt das Formular selbst über
   * `organizerService` — und dessen Endpunkte stehen nur Redaktion und Admins offen. Der
   * Veranstalter-Bereich reicht hier stattdessen einen Vorschlag ein.
   */
  onSubmit?: (payload: CreateOrganizerRequest) => Promise<void>;
  /** Beschriftung des Speicher-Knopfs, wenn „Speichern" nicht passt (z. B. „Vorschlag senden"). */
  saveLabel?: string;
  /** Über einem anderen Modal (Event-Formular) höher stapeln. */
  zIndex?: number;
}

const OrganizerFormModal: React.FC<Props> = ({
  show, organizer, onClose, onSaved, onSubmit, saveLabel, zIndex = 50,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setForm(organizer ? {
      name: organizer.name,
      contactPerson: organizer.contactPerson || '',
      email: organizer.email || '',
      phone: organizer.phone || '',
      website: organizer.website || '',
      address: organizer.address || '',
      street: organizer.street || '',
      postalCode: organizer.postalCode || '',
      city: organizer.city || '',
      bundesland: organizer.bundesland || '',
      radNetUrl: organizer.radNetUrl || '',
      impressumUrl: organizer.impressumUrl || '',
      logoUrl: organizer.logoUrl || '',
      stravaClubUrl: organizer.stravaClubUrl || '',
      instagramUrl: organizer.instagramUrl || '',
      facebookUrl: organizer.facebookUrl || '',
      komootUrl: organizer.komootUrl || '',
      orgKind: organizer.orgKind ?? ('UNKNOWN' as OrganizerKind),
      active: organizer.active,
    } : emptyForm);
  }, [show, organizer]);

  // Fuer welche PLZ das Bundesland zuletzt vorgeschlagen wurde. Die Erkennung laeuft je PLZ genau
  // einmal — bis zum 07.09.2026 lief sie bei jeder Aenderung des Bundeslands erneut und setzte
  // eine Handauswahl sofort zurueck („kann ich auch nicht manuell aendern", Trello). Beim Oeffnen
  // eines bestehenden Veranstalters gilt seine PLZ als erledigt: sein gespeichertes Bundesland
  // wird nicht ungefragt ueberschrieben.
  const plzDetectedFor = useRef<string | null>(null);
  useEffect(() => {
    if (show) plzDetectedFor.current = organizer?.postalCode ?? null;
  }, [show, organizer]);

  useEffect(() => {
    // Die PLZ-Erkennung kennt nur deutsche PLZ. Steht schon ein Land ausserhalb Deutschlands
    // im Feld, darf sie nicht zuschlagen — sonst stempelt "20121" (Mailand) still "Bayern" ueber
    // "Italien", und der Veranstalter landet im falschen Land.
    if (form.postalCode.length !== 5 || plzDetectedFor.current === form.postalCode) return;
    const current = countryOfBundesland(form.bundesland);
    if (current && current !== 'DE') return;
    plzDetectedFor.current = form.postalCode;
    const detected = getPlzBundesland(form.postalCode);
    if (detected) setForm(f => ({ ...f, bundesland: detected }));
  }, [form.postalCode, form.bundesland]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        contactPerson: form.contactPerson || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        website: form.website || undefined,
        address: form.address || undefined,
        street: form.street || undefined,
        postalCode: form.postalCode || undefined,
        city: form.city || undefined,
        bundesland: form.bundesland || undefined,
        radNetUrl: form.radNetUrl || undefined,
        impressumUrl: form.impressumUrl || undefined,
        logoUrl: form.logoUrl || undefined,
        stravaClubUrl: form.stravaClubUrl || undefined,
        instagramUrl: form.instagramUrl || undefined,
        facebookUrl: form.facebookUrl || undefined,
        komootUrl: form.komootUrl || undefined,
        // Nur die Redaktion ordnet ein. Im Veranstalter-Bereich (onSubmit) bleibt das Feld
        // weg — sonst trüge sich ein kommerzieller Anbieter selbst den Vereinspreis ein.
        orgKind: onSubmit ? undefined : form.orgKind,
      };
      if (onSubmit) {
        await onSubmit(payload);
        onClose();
        return;
      }
      const saved = organizer
        ? await organizerService.update(organizer.id, payload)
        : await organizerService.create(payload);
      toast.success(organizer ? 'Veranstalter gespeichert' : 'Veranstalter angelegt');
      onSaved(saved);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Veranstalter konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    background: 'var(--input-bg)',
    border: '1px solid var(--input-border)',
    color: 'var(--text-primary)',
  };

  if (!show) return null;

  return (
    <div className="admin-dialog fixed inset-0 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)', zIndex }}>
      <div className="admin-dialog__panel vv-dialog w-full max-w-lg rounded-xl p-6 max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          {organizer ? 'Veranstalter bearbeiten' : 'Neuer Veranstalter'}
        </h2>
        <div className="mb-6" />
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
              Name <span style={{ color: 'var(--badge-red-text)' }}>*</span>
            </label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
              Ansprechpartner
            </label>
            <input value={form.contactPerson} onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
                E-Mail
              </label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
                Telefon
              </label>
              <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
              Webseite
            </label>
            <input type="url" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
              placeholder="https://..." className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          {/* Zwischenüberschrift, damit die vier Adressfelder als ein Block lesbar bleiben. */}
          <div className="pt-2 border-t" style={{ borderColor: 'var(--surface-border)' }}>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Anschrift
            </div>
          </div>
          {/* Anschrift in zwei Zeilen statt vier: Straße, darunter PLZ · Ort · Land. Eine
              Postanschrift liest man ohnehin als Block, und drei einzeilige Felder untereinander
              schoben Social-Media und den Speichern-Knopf aus dem Bild. */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Straße</label>
            <input value={form.street} onChange={e => setForm(f => ({ ...f, street: e.target.value }))}
              placeholder="Musterstraße 12" className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div className="ofm-adresse grid gap-3" style={{ gridTemplateColumns: '5rem 1fr 1.2fr' }}>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>PLZ</label>
              <input value={form.postalCode} onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))}
                placeholder="12345" className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </div>
            <div style={{ minWidth: 0 }}>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Ort</label>
              <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                placeholder="Musterstadt" className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </div>
            {/* Ohne Merker „automatisch erkannt": die PLZ füllt das Feld sichtbar selbst aus,
                daneben stand nur ein zweites Mal dasselbe — und ein grüner Erfolgshinweis für
                etwas, das noch niemand bestätigt hat. Ändern lässt es sich weiterhin. */}
            <div style={{ minWidth: 0 }}>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
                (Bundes)Land
              </label>
              {form.postalCode.length === 5 ? (
                <input
                  value={form.bundesland}
                  onChange={e => setForm(f => ({ ...f, bundesland: e.target.value }))}
                  placeholder="(Bundes)Land"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={inputStyle}
                />
              ) : (
                <select
                  value={form.bundesland}
                  onChange={e => setForm(f => ({ ...f, bundesland: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={inputStyle}
                >
                  <option value="">— wählen —</option>
                  <BundeslandOptions />
                </select>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 flex items-center gap-2" style={{ color: 'var(--form-label)' }}>
              rad-net.de URL
              {/* Nur für die Redaktion: sie entscheidet, ob der Enricher hier etwas zu holen
                  bekommt. Ein Vereinsvorstand trägt seine rad-net-Seite ein, weil sie zu ihm
                  gehört — was wir daraus lesen, ist nicht seine Frage. */}
              {!onSubmit && (
                <span className="font-normal opacity-60">(für automatische Adress-Anreicherung)</span>
              )}
              {form.radNetUrl && (
                <a href={toExternalUrl(form.radNetUrl)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline" style={{ color: 'var(--accent-warm)' }}>
                  <ExternalLink size={12} aria-hidden /> öffnen
                </a>
              )}
            </label>
            <input type="url" value={form.radNetUrl} onChange={e => setForm(f => ({ ...f, radNetUrl: e.target.value }))}
              placeholder="https://www.rad-net.de/rad-net-portal/vereine.htm?url=..." className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 flex items-center gap-2" style={{ color: 'var(--form-label)' }}>
              Impressum URL
              {/* Nur für die Redaktion: sie entscheidet, ob der Enricher hier etwas zu holen
                  bekommt. Ein Vereinsvorstand trägt seine rad-net-Seite ein, weil sie zu ihm
                  gehört — was wir daraus lesen, ist nicht seine Frage. */}
              {!onSubmit && (
                <span className="font-normal opacity-60">(für automatische Adress-Anreicherung)</span>
              )}
              {form.impressumUrl && (
                <a href={toExternalUrl(form.impressumUrl)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline" style={{ color: 'var(--accent-warm)' }}>
                  <ExternalLink size={12} aria-hidden /> öffnen
                </a>
              )}
            </label>
            <input type="url" value={form.impressumUrl} onChange={e => setForm(f => ({ ...f, impressumUrl: e.target.value }))}
              placeholder="https://..." className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 flex items-center gap-2" style={{ color: 'var(--form-label)' }}>
              Logo URL
              {form.logoUrl && (
                <a href={toExternalUrl(form.logoUrl)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline" style={{ color: 'var(--accent-warm)' }}>
                  <ExternalLink size={12} aria-hidden /> öffnen
                </a>
              )}
            </label>
            {form.logoUrl && (
              <img src={form.logoUrl} alt="Logo Vorschau"
                style={{ height: 48, objectFit: 'contain', marginBottom: 6, borderRadius: 6, background: 'var(--page-bg)', padding: 4 }} />
            )}
            <input type="url" value={form.logoUrl} onChange={e => setForm(f => ({ ...f, logoUrl: e.target.value }))}
              placeholder="https://..." className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>

          <div className="pt-2 border-t flex items-center gap-2 mb-2" style={{ borderColor: 'var(--surface-border)' }}>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Social Media
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 flex items-center gap-2" style={{ color: 'var(--form-label)' }}>
              Strava (Club)
              {form.stravaClubUrl && (
                <a href={toExternalUrl(form.stravaClubUrl)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline" style={{ color: 'var(--accent-warm)' }}><ExternalLink size={12} aria-hidden /> öffnen</a>
              )}
            </label>
            <input type="url" value={form.stravaClubUrl}
              onChange={e => setForm(f => ({ ...f, stravaClubUrl: e.target.value }))}
              placeholder="https://www.strava.com/clubs/..." className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 flex items-center gap-2" style={{ color: 'var(--form-label)' }}>
              Instagram
              {form.instagramUrl && (
                <a href={toExternalUrl(form.instagramUrl)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline" style={{ color: 'var(--accent-warm)' }}><ExternalLink size={12} aria-hidden /> öffnen</a>
              )}
            </label>
            <input type="url" value={form.instagramUrl}
              onChange={e => setForm(f => ({ ...f, instagramUrl: e.target.value }))}
              placeholder="https://www.instagram.com/..." className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 flex items-center gap-2" style={{ color: 'var(--form-label)' }}>
              Facebook
              {form.facebookUrl && (
                <a href={toExternalUrl(form.facebookUrl)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline" style={{ color: 'var(--accent-warm)' }}><ExternalLink size={12} aria-hidden /> öffnen</a>
              )}
            </label>
            <input type="url" value={form.facebookUrl}
              onChange={e => setForm(f => ({ ...f, facebookUrl: e.target.value }))}
              placeholder="https://www.facebook.com/..." className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 flex items-center gap-2" style={{ color: 'var(--form-label)' }}>
              Komoot
              {form.komootUrl && (
                <a href={toExternalUrl(form.komootUrl)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline" style={{ color: 'var(--accent-warm)' }}><ExternalLink size={12} aria-hidden /> öffnen</a>
              )}
            </label>
            <input type="url" value={form.komootUrl}
              onChange={e => setForm(f => ({ ...f, komootUrl: e.target.value }))}
              placeholder="https://www.komoot.com/user/..." className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>

          {!onSubmit && (
            <div className="pt-2 border-t" style={{ borderColor: 'var(--surface-border)' }}>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
                Einordnung
                <span className="font-normal opacity-60"> (bestimmt den Preis)</span>
              </label>
              <select
                value={form.orgKind}
                onChange={e => setForm(f => ({ ...f, orgKind: e.target.value as OrganizerKind }))}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={inputStyle}
              >
                <option value="UNKNOWN">Noch nicht eingeordnet</option>
                <option value="CLUB">Verein</option>
                <option value="COMMERCIAL">Kommerzieller Anbieter</option>
              </select>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Vereine zahlen weniger. Solange nichts eingeordnet ist, sieht der Veranstalter
                beide Preise und wählt selbst. Die Einordnung hier gilt.
              </p>
            </div>
          )}

          {organizer && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="orgActive" checked={form.active}
                onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
              <label htmlFor="orgActive" className="text-sm" style={{ color: 'var(--text-primary)' }}>Aktiv</label>
            </div>
          )}
        </div>
        <div className="admin-dialog__aktionen flex justify-end gap-3 mt-6">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg"
            style={{ color: 'var(--text-secondary)', background: 'var(--page-bg)', border: '1px solid var(--surface-border)' }}>
            Abbrechen
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-brand-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Speichern...' : (saveLabel ?? 'Speichern')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganizerFormModal;
